import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Factory, Eye, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Produccion() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [receptions, setReceptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ reception_id: "", shift: "matutino", cream_kg: "", curd_kg: "", notes: "" });
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    const [p, r] = await Promise.all([api.get("/productions"), api.get("/receptions")]);
    setItems(p.data);
    setReceptions(r.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.reception_id) return toast.error("Selecciona el lote");
    try {
      const { data } = await api.post("/productions", {
        ...form,
        cream_kg: parseFloat(form.cream_kg) || 0,
        curd_kg: parseFloat(form.curd_kg) || 0,
      });
      toast.success(`Lotes generados: ${data.cream_folio} y ${data.curd_folio}`);
      setForm({ reception_id: "", shift: "matutino", cream_kg: "", curd_kg: "", notes: "" });
      setOpen(false);
      load();
    } catch { toast.error("Error al guardar"); }
  };

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="produccion-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900">Producción</h1>
          <p className="text-sm text-stone-500 mt-1">Genera lotes de crema (CRE-) y cuajada (CUA-) a partir de la materia prima.</p>
        </div>
        <Button onClick={() => setOpen(!open)} data-testid="new-production-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
          <Plus className="w-4 h-4 mr-1.5" /> Nuevo lote
        </Button>
      </div>

      {open && (
        <Card className="p-6 border-stone-200 bg-white shadow-sm">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="lg:col-span-2">
              <Label className="text-xs font-outfit font-semibold uppercase tracking-wider text-stone-600">Lote MP *</Label>
              <Select value={form.reception_id} onValueChange={(v) => setForm({ ...form, reception_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="production-reception"><SelectValue placeholder="Lote MP" /></SelectTrigger>
                <SelectContent>{receptions.map((r) => <SelectItem key={r.id} value={r.id}>{r.folio} · {r.supplier_name} · {r.liters} L</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-outfit font-semibold uppercase tracking-wider text-stone-600">Turno</Label>
              <Select value={form.shift} onValueChange={(v) => setForm({ ...form, shift: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="matutino">Matutino</SelectItem>
                  <SelectItem value="vespertino">Vespertino</SelectItem>
                  <SelectItem value="nocturno">Nocturno</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div></div>
            <div><Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">Crema producida (kg)</Label><Input data-testid="production-cream" type="number" step="0.01" value={form.cream_kg} onChange={(e) => setForm({ ...form, cream_kg: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">Cuajada producida (kg)</Label><Input data-testid="production-curd" type="number" step="0.01" value={form.curd_kg} onChange={(e) => setForm({ ...form, curd_kg: e.target.value })} className="mt-1.5" /></div>
            <div className="md:col-span-2"><Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">Notas</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
            <div className="lg:col-span-4 flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="production-submit-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Generar lotes CRE/CUA</Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="border-stone-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full" data-testid="productions-table">
          <thead>
            <tr className="bg-stone-100 border-b border-stone-200">
              {["Fecha", "Lote MP", "Lote Crema", "Crema (kg)", "Rend. Crema", "Lote Cuajada", "Cuajada (kg)", "Rend. Cuajada", "Operador", "Acciones"].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs font-outfit font-semibold uppercase tracking-wider text-stone-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-stone-500">Sin producciones.</td></tr>
            ) : items.map((p) => (
              <tr key={p.id} className="border-b border-stone-100 hover:bg-stone-50">
                <td className="px-4 py-3 text-sm text-stone-700">{new Date(p.created_at).toLocaleString("es-MX")}</td>
                <td className="px-4 py-3 text-sm text-stone-700">{p.reception_folio}</td>
                <td className="px-4 py-3 text-sm font-medium text-[#1E3A8A]"><div className="flex items-center gap-1.5"><Factory className="w-3.5 h-3.5" />{p.cream_folio}</div></td>
                <td className="px-4 py-3 text-sm tabular-nums">{p.cream_kg}</td>
                <td className="px-4 py-3 text-sm tabular-nums text-emerald-700">{p.cream_yield_pct}%</td>
                <td className="px-4 py-3 text-sm font-medium text-[#1E3A8A]">{p.curd_folio}</td>
                <td className="px-4 py-3 text-sm tabular-nums">{p.curd_kg}</td>
                <td className="px-4 py-3 text-sm tabular-nums text-emerald-700">{p.curd_yield_pct}%</td>
                <td className="px-4 py-3 text-sm text-stone-700">{p.operator}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => navigate(`/trazabilidad?lote=${encodeURIComponent(p.reception_folio)}`)} title="Ver trazabilidad" className="p-1.5 rounded-md text-stone-500 hover:text-[#1E3A8A] hover:bg-[#1E3A8A]/5"><Eye className="w-4 h-4" /></button>
                    <button onClick={() => setToDelete(p)} data-testid={`delete-prod-${p.id}`} title="Eliminar" className="p-1.5 rounded-md text-stone-500 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar producción {toDelete?.cream_folio} / {toDelete?.curd_folio}?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminarán también los lotes de crema y cuajada generados. No se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={async () => { try { await api.delete(`/productions/${toDelete.id}`); toast.success("Producción eliminada"); setToDelete(null); load(); } catch { toast.error("Error"); } }} className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
